// CMS Admin Users Management Page
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useCMS } from './CMSContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { 
  Plus, Edit, Trash2, Shield, Mail, Calendar, RefreshCw, CheckCircle, XCircle, Send
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CMSAdmins = () => {
  const { getAuthHeaders, admin: currentAdmin } = useCMS();
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialog, setCreateDialog] = useState(false);
  const [editDialog, setEditDialog] = useState({ open: false, admin: null });
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    role: 'read_only',
    send_password_email: true  // Default to sending password change email
  });

  const fetchAdmins = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/cms/admins`, { headers: getAuthHeaders() });
      setAdmins(res.data.admins);
    } catch (err) {
      toast.error('Failed to load admin users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAdmins();
  }, []);

  const handleCreate = async () => {
    try {
      const res = await axios.post(`${API}/cms/admins`, formData, { headers: getAuthHeaders() });
      if (res.data.email_sent) {
        toast.success('Admin aangemaakt en wachtwoord wijzigen email verzonden');
      } else if (formData.send_password_email) {
        toast.success('Admin aangemaakt (email verzenden mislukt - controleer SMTP instellingen)');
      } else {
        toast.success('Admin user created');
      }
      setCreateDialog(false);
      setFormData({ email: '', password: '', first_name: '', last_name: '', role: 'read_only', send_password_email: true });
      fetchAdmins();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create admin');
    }
  };

  const handleUpdate = async () => {
    try {
      const updateData = {
        role: editDialog.admin.role,
        is_active: editDialog.admin.is_active,
        first_name: editDialog.admin.first_name,
        last_name: editDialog.admin.last_name
      };
      await axios.patch(`${API}/cms/admins/${editDialog.admin.admin_id}`, updateData, { 
        headers: getAuthHeaders() 
      });
      toast.success('Admin user updated');
      setEditDialog({ open: false, admin: null });
      fetchAdmins();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update admin');
    }
  };

  const handleDelete = async (adminId) => {
    if (!confirm('Are you sure you want to delete this admin user?')) return;
    
    try {
      await axios.delete(`${API}/cms/admins/${adminId}`, { headers: getAuthHeaders() });
      toast.success('Admin user deleted');
      fetchAdmins();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete admin');
    }
  };

  const formatDate = (date) => {
    if (!date) return 'Never';
    return new Date(date).toLocaleString();
  };

  const roleColors = {
    super_admin: 'bg-red-500/20 text-red-400',
    support: 'bg-blue-500/20 text-blue-400',
    finance: 'bg-green-500/20 text-green-400',
    account_manager: 'bg-purple-500/20 text-purple-400',
    read_only: 'bg-slate-500/20 text-slate-400'
  };

  const roleLabels = {
    super_admin: 'Super Admin',
    support: 'Support',
    finance: 'Finance',
    account_manager: 'Account Manager',
    read_only: 'Read Only'
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Admin Users</h1>
          <p className="text-slate-400">Manage CMS administrator accounts</p>
        </div>
        <div className="flex gap-2">
          <Dialog open={createDialog} onOpenChange={setCreateDialog}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="w-4 h-4 mr-2" />
                Add Admin
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white border-gray-200 text-gray-900">
              <DialogHeader>
                <DialogTitle>Create Admin User</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-gray-700">First Name</Label>
                    <Input
                      value={formData.first_name}
                      onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                      className="bg-white border-gray-300 text-gray-900"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-700">Last Name</Label>
                    <Input
                      value={formData.last_name}
                      onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                      className="bg-white border-gray-300 text-gray-900"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-700">Email *</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="bg-white border-gray-300 text-gray-900"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-700">Password *</Label>
                  <Input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                    className="bg-white border-gray-300 text-gray-900"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-700">Role</Label>
                  <Select value={formData.role} onValueChange={(v) => setFormData({...formData, role: v})}>
                    <SelectTrigger className="bg-white border-gray-300 text-gray-900">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="super_admin">Super Admin</SelectItem>
                      <SelectItem value="support">Support</SelectItem>
                      <SelectItem value="finance">Finance</SelectItem>
                      <SelectItem value="account_manager">Account Manager</SelectItem>
                      <SelectItem value="read_only">Read Only</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center space-x-2 py-2">
                  <Checkbox
                    id="send_password_email"
                    checked={formData.send_password_email}
                    onCheckedChange={(checked) => setFormData({...formData, send_password_email: checked})}
                  />
                  <Label htmlFor="send_password_email" className="text-gray-700 text-sm cursor-pointer flex items-center gap-2">
                    <Send className="w-4 h-4" />
                    Stuur wachtwoord wijzigen email
                  </Label>
                </div>
                <p className="text-xs text-gray-500 -mt-1">
                  De nieuwe admin ontvangt een email om zijn/haar wachtwoord te wijzigen
                </p>
                <Button onClick={handleCreate} className="w-full">
                  Create Admin User
                </Button>
              </div>
            </DialogContent>
          </Dialog>
          <Button variant="outline" onClick={fetchAdmins} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Admins Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {loading ? (
          <div className="col-span-full flex justify-center py-8">
            <RefreshCw className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : admins.length === 0 ? (
          <div className="col-span-full text-center py-8 text-slate-400">
            No admin users found
          </div>
        ) : (
          admins.map((admin) => (
            <Card key={admin.admin_id} className="bg-slate-800 border-slate-700">
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-slate-700 flex items-center justify-center">
                      <Shield className="w-6 h-6 text-slate-400" />
                    </div>
                    <div>
                      <p className="font-medium">{admin.first_name} {admin.last_name}</p>
                      <p className="text-sm text-slate-400">{admin.email}</p>
                    </div>
                  </div>
                  {admin.admin_id !== currentAdmin?.admin_id && (
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setEditDialog({ open: true, admin: { ...admin } })}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(admin.admin_id)}
                      >
                        <Trash2 className="w-4 h-4 text-red-400" />
                      </Button>
                    </div>
                  )}
                </div>

                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-400">Role</span>
                    <Badge className={roleColors[admin.role]}>
                      {roleLabels[admin.role]}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-400">Status</span>
                    {admin.is_active ? (
                      <Badge className="bg-green-500/20 text-green-400">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Active
                      </Badge>
                    ) : (
                      <Badge variant="destructive">
                        <XCircle className="w-3 h-3 mr-1" />
                        Disabled
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-400">Last Login</span>
                    <span className="text-sm">{formatDate(admin.last_login)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-400">Created</span>
                    <span className="text-sm">{formatDate(admin.created_at)}</span>
                  </div>
                </div>

                {admin.admin_id === currentAdmin?.admin_id && (
                  <div className="mt-4 pt-4 border-t border-slate-700">
                    <Badge variant="outline" className="text-primary border-primary">
                      This is you
                    </Badge>
                  </div>
                )}
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Edit Dialog */}
      <Dialog open={editDialog.open} onOpenChange={(open) => setEditDialog({ ...editDialog, open })}>
        <DialogContent className="bg-white border-gray-200 text-gray-900">
          <DialogHeader>
            <DialogTitle className="text-gray-900">Edit Admin User</DialogTitle>
          </DialogHeader>
          {editDialog.admin && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-gray-700">First Name</Label>
                  <Input
                    value={editDialog.admin.first_name || ''}
                    onChange={(e) => setEditDialog({
                      ...editDialog, 
                      admin: {...editDialog.admin, first_name: e.target.value}
                    })}
                    className="bg-white border-gray-300 text-gray-900"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-700">Last Name</Label>
                  <Input
                    value={editDialog.admin.last_name || ''}
                    onChange={(e) => setEditDialog({
                      ...editDialog, 
                      admin: {...editDialog.admin, last_name: e.target.value}
                    })}
                    className="bg-white border-gray-300 text-gray-900"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-gray-700">Role</Label>
                <Select 
                  value={editDialog.admin.role} 
                  onValueChange={(v) => setEditDialog({
                    ...editDialog, 
                    admin: {...editDialog.admin, role: v}
                  })}
                >
                  <SelectTrigger className="bg-white border-gray-300 text-gray-900">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="super_admin">Super Admin</SelectItem>
                    <SelectItem value="support">Support</SelectItem>
                    <SelectItem value="finance">Finance</SelectItem>
                    <SelectItem value="account_manager">Account Manager</SelectItem>
                    <SelectItem value="read_only">Read Only</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={editDialog.admin.is_active}
                  onChange={(e) => setEditDialog({
                    ...editDialog, 
                    admin: {...editDialog.admin, is_active: e.target.checked}
                  })}
                  className="rounded border-gray-300"
                />
                <Label htmlFor="is_active" className="text-gray-700">Account Active</Label>
              </div>
              <Button onClick={handleUpdate} className="w-full">
                Save Changes
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CMSAdmins;
